import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Badge } from '@skupreme/shared/ui';
import { FormItem } from '@skupreme/shared/ui/form';
import { CountryDropdownStyle, type IDropdownOption } from '@elements/Dropdown';
import { ConnectPopup } from './ConnectPopup';

/** A region to pick, carrying the marketplace row it connects. */
type RegionOption<T> = IDropdownOption & {
  item: T;
  connected_label?: unknown;
};

type RegionPopupProps<T> = {
  open: boolean;
  /** Gets the picked region's row, or nothing when the user backs out. */
  closeModal: (item?: T) => void;
  regions: RegionOption<T>[];
  modalClassName?: string;
  title?: string;
  closeable?: boolean;
  defaultRegion?: string | null;
};

export function RegionPopup<T>({
  open,
  closeModal,
  regions,
  modalClassName = '',
  title = 'Connect your marketplace',
  closeable = true,
  defaultRegion = null,
}: RegionPopupProps<T>) {
  const [region, setRegion] = useState('');
  const [error, setError] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: each opening starts again from the default region.
  useEffect(() => {
    setRegion(defaultRegion || '');
    setError('');
  }, [open, defaultRegion]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!region) {
      setError('Marketplace Region is required');
      return;
    }
    const picked = regions.find(x => x.value === region);
    closeModal(picked?.item);
  };

  const regionOptions = useMemo(
    () =>
      regions.map(option => ({
        ...option,
        trailing: option.connected_label ? (
          <Badge tone="danger">Override</Badge>
        ) : undefined,
      })),
    [regions],
  );

  return (
    <ConnectPopup
      open={open}
      title={title}
      onClose={() => closeModal()}
      onSubmit={onSubmit}
      closeable={closeable}
      className={modalClassName}
    >
      <FormItem label="Marketplace Region" error={error} isRequired>
        <CountryDropdownStyle
          selection
          options={regionOptions}
          value={region}
          onChange={(_, { value }) => {
            setRegion(value as string);
            setError('');
          }}
          className="sk:w-full"
          placeholder="Marketplace Region"
          aria-label="Marketplace Region"
          error={!!error}
        />
      </FormItem>
    </ConnectPopup>
  );
}
